import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ExternalLink, Download, AlertCircle, CheckCircle2, X, Users } from 'lucide-react';
import { useCustomers } from '@/hooks/useCustomers';
import { useServiceFields } from '@/hooks/useServiceFields';
import { useToast } from '@/hooks/use-toast';
import SimplePDFViewer from '@/components/SimplePDFViewer';

interface ProductionReviewInterfaceProps {
  queueItem: any;
  onUpdate: (data: any) => Promise<void>;
  onClose: () => void;
}

const ProductionReviewInterface: React.FC<ProductionReviewInterfaceProps> = ({
  queueItem,
  onUpdate,
  onClose
}) => {
  // Core state
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  // Base form data (core bill fields)
  const [baseFormData, setBaseFormData] = useState({
    amount: '',
    billDate: '',
    supplierName: '',
    accountNumber: '',
    serviceType: 'electric',
    billNumber: '',
    notes: ''
  });

  // Dynamic form data (service-specific fields)
  const [dynamicFormData, setDynamicFormData] = useState<Record<string, any>>({});

  const { toast } = useToast();

  // Hooks
  const { customers, isLoading: customersLoading } = useCustomers();
  const { fields: serviceFields, loading: fieldsLoading, serviceTypes } = useServiceFields(selectedCustomer);

  // Simple PDF URL - use direct Supabase storage URLs
  const pdfUrl = useMemo(() => {
    const filePath = queueItem?.file_path;
    if (!filePath) return null;
    
    if (filePath.startsWith('http')) {
      return filePath;
    }
    
    return `https://dyftqspuiyabqzjslwyw.supabase.co/storage/v1/object/public/bills/${filePath}`;
  }, [queueItem?.file_path]);

  // Initialize form data from queue item
  useEffect(() => {
    if (!queueItem?.id) return;

    // Set customer if pre-assigned
    if (queueItem.customer_id) {
      setSelectedCustomer(queueItem.customer_id);
    }

    // Initialize base form data
    const parsedData = queueItem.parsed_data || {};
    setBaseFormData({
      amount: parsedData.total_amount?.toString() || '',
      billDate: parsedData.bill_date || '',
      supplierName: parsedData.supplier_name || '',
      accountNumber: parsedData.account_number || '',
      serviceType: parsedData.service_type || 'electric',
      billNumber: parsedData.bill_number || '',
      notes: parsedData.notes || ''
    });

    console.log('📋 Form initialized with data:', {
      queueItemId: queueItem.id,
      customerId: queueItem.customer_id,
      parsedData
    });
  }, [queueItem?.id, queueItem?.customer_id, queueItem?.parsed_data]);

  // Initialize dynamic form data when service fields change
  useEffect(() => {
    if (serviceFields.length > 0) {
      const initialDynamicData: Record<string, any> = {};
      serviceFields.forEach(field => {
        initialDynamicData[field.field_key] = dynamicFormData[field.field_key] || '';
      });
      setDynamicFormData(initialDynamicData);
    }
  }, [serviceFields]);

  // Form update handlers
  const updateBaseFormData = useCallback((field: string, value: any) => {
    setBaseFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const updateDynamicFormData = useCallback((field: string, value: any) => {
    setDynamicFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Validation
  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {};
    
    if (!selectedCustomer) {
      errors.customer = 'Customer selection is required';
    }
    
    if (!baseFormData.amount || parseFloat(baseFormData.amount) <= 0) {
      errors.amount = 'Valid amount is required';
    }
    
    if (!baseFormData.billDate) {
      errors.billDate = 'Bill date is required';
    }
    
    if (!baseFormData.supplierName.trim()) {
      errors.supplierName = 'Supplier name is required';
    }

    // Validate dynamic fields
    serviceFields.forEach(field => {
      if (field.is_required && !dynamicFormData[field.field_key]?.toString().trim()) {
        errors[field.field_key] = `${field.label} is required`;
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [selectedCustomer, baseFormData, serviceFields, dynamicFormData]);

  // Save handler
  const handleSave = useCallback(async (complete = false) => {
    if (!validateForm()) {
      console.log('❌ Validation failed:', validationErrors);
      return;
    }
    
    setSaving(true);
    try {
      const updateData = {
        ...baseFormData,
        ...dynamicFormData,
        customer_id: selectedCustomer,
        total_amount: parseFloat(baseFormData.amount),
        status: complete ? 'completed' : 'in_progress'
      };
      
      await onUpdate(updateData);
      console.log('✅ Bill data saved successfully');
      
      toast({
        title: complete ? "Review Completed" : "Progress Saved",
        description: complete ? "Bill has been completed successfully." : "Your progress has been saved.",
      });
      
      if (complete && onClose) {
        onClose();
      }
    } catch (error) {
      console.error('❌ Save error:', error);
      toast({
        title: "Save Failed",
        description: "There was an error saving your changes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [baseFormData, dynamicFormData, selectedCustomer, validateForm, onUpdate, onClose, toast, validationErrors]);

  const isCustomerLocked = Boolean(queueItem?.customer_id);
  const selectedCustomerData = customers?.find(c => c.id === selectedCustomer);
  const hasErrors = Object.keys(validationErrors).length > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-screen p-4">
      {/* PDF Viewer Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={onClose}>
            ← Back to Queue
          </Button>
          <h1 className="text-xl font-semibold">
            {queueItem?.original_filename || 'PECO_BILL_FOR_TESTING.pdf'}
          </h1>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => handleSave(false)} disabled={saving}>
              Save Progress
            </Button>
            <Button size="sm" onClick={() => handleSave(true)} disabled={saving || hasErrors}>
              Complete Review
            </Button>
          </div>
        </div>

        <div className="h-[calc(100vh-120px)]">
          <SimplePDFViewer 
            pdfUrl={pdfUrl || undefined} 
            fileName={queueItem?.original_filename || "Bill Document"} 
          />
        </div>
      </div>

      {/* Form Section */}
      <div className="space-y-6">
        {/* Customer Assignment */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Customer Assignment *
              {isCustomerLocked && (
                <Badge variant="secondary">Pre-assigned</Badge>
              )}
              {selectedCustomer && serviceTypes.length > 0 && (
                <div className="flex gap-1">
                  {serviceTypes.map(type => (
                    <Badge key={type} variant="outline" className="text-xs">
                      {type}
                    </Badge>
                  ))}
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {customersLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading customers...</span>
              </div>
            ) : (
              <Select 
                value={selectedCustomer} 
                onValueChange={setSelectedCustomer}
                disabled={isCustomerLocked}
              >
                <SelectTrigger className={validationErrors.customer ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select a customer..." />
                </SelectTrigger>
                <SelectContent>
                  {customers?.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  )) || []}
                </SelectContent>
              </Select>
            )}
            {validationErrors.customer && (
              <Alert className="mt-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{validationErrors.customer}</AlertDescription>
              </Alert>
            )}
            {isCustomerLocked && selectedCustomerData && (
              <div className="mt-2 text-sm text-muted-foreground">
                Pre-assigned to: <strong>{selectedCustomerData.name}</strong>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Base Bill Information */}
        <Card>
          <CardHeader>
            <CardTitle>Bill Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  value={baseFormData.amount}
                  onChange={(e) => updateBaseFormData('amount', e.target.value)}
                  className={validationErrors.amount ? 'border-destructive' : ''}
                  placeholder="Enter amount"
                />
                {validationErrors.amount && (
                  <p className="text-destructive text-sm mt-1">{validationErrors.amount}</p>
                )}
              </div>
              <div>
                <Label htmlFor="billDate">Bill Date *</Label>
                <Input
                  id="billDate"
                  type="date"
                  value={baseFormData.billDate}
                  onChange={(e) => updateBaseFormData('billDate', e.target.value)}
                  className={validationErrors.billDate ? 'border-destructive' : ''}
                />
                {validationErrors.billDate && (
                  <p className="text-destructive text-sm mt-1">{validationErrors.billDate}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="supplierName">Supplier Name *</Label>
              <Input
                id="supplierName"
                value={baseFormData.supplierName}
                onChange={(e) => updateBaseFormData('supplierName', e.target.value)}
                className={validationErrors.supplierName ? 'border-destructive' : ''}
                placeholder="Enter supplier name"
              />
              {validationErrors.supplierName && (
                <p className="text-destructive text-sm mt-1">{validationErrors.supplierName}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="accountNumber">Account Number</Label>
                <Input
                  id="accountNumber"
                  value={baseFormData.accountNumber}
                  onChange={(e) => updateBaseFormData('accountNumber', e.target.value)}
                  placeholder="Enter account number"
                />
              </div>
              <div>
                <Label htmlFor="serviceType">Service Type</Label>
                <Select
                  value={baseFormData.serviceType}
                  onValueChange={(value) => updateBaseFormData('serviceType', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="electric">Electric</SelectItem>
                    <SelectItem value="gas">Gas</SelectItem>
                    <SelectItem value="water">Water</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="billNumber">Bill Number</Label>
              <Input
                id="billNumber"
                value={baseFormData.billNumber}
                onChange={(e) => updateBaseFormData('billNumber', e.target.value)}
                placeholder="Enter bill number"
              />
            </div>
          </CardContent>
        </Card>

        {/* Dynamic Service-Specific Fields */}
        {serviceFields.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {serviceTypes.join(', ').toUpperCase()} Service Details
                {fieldsLoading && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {serviceFields.map((field) => (
                <div key={field.field_key}>
                  <Label htmlFor={field.field_key}>
                    {field.label} {field.is_required && '*'}
                  </Label>
                  <Input
                    id={field.field_key}
                    type={field.input_type === 'date' ? 'date' : field.input_type === 'number' ? 'number' : 'text'}
                    value={dynamicFormData[field.field_key] || ''}
                    onChange={(e) => updateDynamicFormData(field.field_key, e.target.value)}
                    className={validationErrors[field.field_key] ? 'border-destructive' : ''}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                  />
                  {field.help_text && (
                    <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>
                  )}
                  {validationErrors[field.field_key] && (
                    <p className="text-destructive text-sm mt-1">{validationErrors[field.field_key]}</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={baseFormData.notes}
              onChange={(e) => updateBaseFormData('notes', e.target.value)}
              placeholder="Add any notes..."
              rows={3}
            />
          </CardContent>
        </Card>

        {/* Validation Summary */}
        {hasErrors && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Please fix the validation errors above before completing the review.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default ProductionReviewInterface;